import { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { AlertTriangle, Trash2, Eye, RefreshCw, ShieldAlert, Database, Download, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACCOUNTING_TX = ['journal_entries', 'accounting_pending'];
const TREASURY_TX = ['treasury_movements', 'treasury_payment_orders', 'treasury_collections', 'treasury_transfers', 'treasury_adjustments', 'treasury_checks'];
const RESIDENTS_TX = ['resident_invoices', 'resident_cc', 'resident_advances', 'billing_runs', 'insurer_cc', 'insurer_receipts'];

// Orden de los grupos en la UI (los módulos en test primero, luego datos reales)
const GROUP_ORDER = ['Reset coordinado', 'Contabilidad', 'Tesorería', 'Residentes', 'Compras', 'Enfermería', 'Sistema'];

const TARGET_DESCRIPTIONS = {
  movements: 'Historial de entradas/salidas. NO afecta el stock vigente (vive en articles + stock_lots).',
  audit_logs: 'Bitácora de acciones de usuarios. Perdés trazabilidad histórica.',
  internal_requests: 'OPI generadas por las áreas. Si borrás todas, perdés el historial pero los stock_lots ya despachados quedan intactos.',
  internal_returns: 'Notas de Crédito Interna (NCI / devoluciones).',
  internal_consumptions: 'Vouchers MI (Consumo Interno por residente / sala).',
  nursing_external_receptions: 'Recepciones Externas de Medicación (REM): ingresos traídos por familiares. NO toca los stock_lots, solo borra el documento maestro de la recepción.',
  purchase_requests: 'Notas de Pedido (NP) emitidas a Compras.',
  purchase_orders: 'Órdenes de Compra (OC) a proveedores.',
  receptions: 'Recepciones de mercadería (origen de stock_lots, pero estos NO se tocan).',
  articles: '⚠️ Borra artículos del catálogo. Cascada: también borra sus stock_lots y movements asociados (para no dejar huérfanos).',
  stock_lots: '⚠️ ATENCIÓN: borra lotes vigentes, perdés stock real. Usá "RESET de Stock" si querés dejar todo en cero.',
  attachments: 'Archivos adjuntos (NP / OC). Solo borra los metadatos; los blobs viven en object storage.',
  user_sessions: 'Sesiones activas (los usuarios deberán volver a loguearse).',
  users: 'Usuarios. NO podés borrarte a vos mismo ni al último admin. Sus sesiones también se borran.',
  stock_reset: '💣 RESET COMPLETO de stock: setea articles.stock=0, borra stock_lots y movements. ¡Irreversible!',
  // Contabilidad
  journal_entries: 'Asientos del Libro Diario (partida doble). NO toca el Plan de Cuentas ni los Mapeos (son config real).',
  accounting_pending: 'Cola de documentos que no pudieron asentarse por falta de mapeos. Borrar solo limpia la cola.',
  accounting_reset: '🧹 RESET transaccional de Contabilidad: borra TODOS los asientos + la cola de pendientes. CONSERVA el Plan de Cuentas y los Mapeos.',
  // Tesorería
  treasury_movements: 'Libro de movimientos de cajas/bancos (income/expense por cash_bank). NO borra los maestros.',
  treasury_payment_orders: 'Órdenes de Pago emitidas a proveedores desde Tesorería.',
  treasury_collections: 'Cobranzas (ingresos de fondos).',
  treasury_transfers: 'Transferencias entre cajas/bancos.',
  treasury_adjustments: 'Ajustes manuales sobre cajas/bancos.',
  treasury_checks: 'Movimientos de cheques (emitidos/recibidos, propio/terceros).',
  treasury_reset: '🧹 RESET transaccional de Tesorería: borra las 6 colecciones de transacciones y restablece current_balance=initial_balance + acumulados de conceptos en 0. Las CAJAS/BANCOS y CONCEPTOS (maestros) NO se tocan.',
  residents_reset: '🧹 RESET transaccional de Facturación: borra FC/NC, CC de residentes, anticipos, corridas masivas, CC y recibos de Obras Sociales, y reinicia la numeración (próxima FC #1). CONSERVA fichas, servicios, asignaciones y Obras Sociales (maestros).',
  test_modules_reset: '🧹💣 RESET de MÓDULOS EN TEST: ejecuta en orden seguro el reset transaccional de Contabilidad + Tesorería + Residentes. Deja Compras y Enfermería intactos y conserva TODOS los maestros (residentes, cajas, plan de cuentas, mapeos, medicamentos). Es lo que usás para dejar producción limpia.',
};

const REAL_DATA_NOTE = '⚠️ DATOS REALES / maestro — normalmente NO se purga.';

function Maintenance() {
  const { user } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null); // target object
  const [filters, setFilters] = useState({});
  const [preview, setPreview] = useState(null); // {matched, target, filters}
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [purging, setPurging] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]); // for users target
  const [fullBackupExporting, setFullBackupExporting] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!isAdmin) return;
    loadStats();
  }, [isAdmin]);

  const loadStats = async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/maintenance/stats`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error('No se pudo cargar estadísticas');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const openTarget = (target) => {
    setSelected(target);
    setFilters({});
    setSelectedUsers([]);
    setPreview(null);
  };

  const buildFiltersPayload = () => {
    if (!selected) return {};
    if (selected.key === 'users') {
      return { user_ids: selectedUsers };
    }
    return filters;
  };

  const onPreview = async () => {
    if (!selected) return;
    setPreviewing(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/maintenance/preview`,
        { target: selected.key, filters: buildFiltersPayload() },
        { withCredentials: true }
      );
      setPreview(r.data);
      toast.success(`${r.data.matched} registro(s) coinciden con los filtros`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en preview');
    } finally {
      setPreviewing(false);
    }
  };

  const onAskConfirm = () => {
    if (!preview || preview.matched === 0) {
      toast.error('Primero hacé "Vista previa" para confirmar que hay registros para borrar');
      return;
    }
    setConfirmText('');
    setConfirmOpen(true);
  };

  const onFullBackup = async () => {
    setFullBackupExporting(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/export/all`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `backup_hogar_gestion_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
      toast.success('Backup completo descargado');
    } catch (error) {
      // Si el backend devolvió un blob con JSON, leerlo para mostrar el detalle real
      let msg = 'Error al exportar datos';
      try {
        if (error.response?.data instanceof Blob) {
          const txt = await error.response.data.text();
          const parsed = JSON.parse(txt);
          msg = parsed.detail || msg;
        } else if (error.response?.data?.detail) {
          msg = error.response.data.detail;
        }
      } catch {}
      toast.error(msg);
    } finally {
      setFullBackupExporting(false);
    }
  };

  const downloadBackup = async () => {
    if (!selected) return false;
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/maintenance/export`,
        { target: selected.key, filters: buildFiltersPayload() },
        { withCredentials: true, responseType: 'blob' }
      );
      const blob = new Blob([resp.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = resp.headers['content-disposition'] || '';
      const match = cd.match(/filename="([^"]+)"/);
      a.download = match ? match[1] : `backup_${selected.key}_${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return a.download;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar el backup');
      return false;
    }
  };

  const onExport = async () => {
    if (!selected) return;
    setExporting(true);
    const name = await downloadBackup();
    if (name) toast.success(`Backup descargado: ${name}`);
    setExporting(false);
  };

  const onPurge = async () => {
    if (confirmText !== 'BORRAR') {
      toast.error("Tipeá 'BORRAR' tal cual (mayúsculas) para confirmar");
      return;
    }
    setPurging(true);
    try {
      // Red de seguridad obligatoria: descargar el backup ANTES de borrar.
      const backupName = await downloadBackup();
      if (!backupName) {
        toast.error('No se pudo generar el backup. No se borró nada.');
        setPurging(false);
        return;
      }
      toast.success(`Backup de seguridad descargado: ${backupName}`);
      const r = await axios.post(
        `${BACKEND_URL}/api/maintenance/purge`,
        { target: selected.key, filters: buildFiltersPayload(), confirm: 'BORRAR' },
        { withCredentials: true }
      );
      toast.success(
        `${selected.label} purgado. Coincidían ${r.data.matched}. Resultado: ${JSON.stringify(r.data.result)}`
      );
      setConfirmOpen(false);
      setPreview(null);
      setFilters({});
      setSelectedUsers([]);
      await loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al purgar');
    } finally {
      setPurging(false);
    }
  };

  const supportedFilters = selected?.filters || [];
  const counts = data?.counts || {};

  const totalEligible = useMemo(() => {
    if (!data?.targets) return 0;
    return data.targets
      .filter(t => t.key !== 'stock_reset' && t.key !== 'treasury_reset')
      .reduce((acc, t) => acc + (counts[t.key] || 0), 0);
  }, [data, counts]);

  if (!isAdmin) {
    return (
      <div className="p-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-500" />
              Acceso restringido
            </CardTitle>
            <CardDescription>Solo administradores pueden acceder a Mantenimiento.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="maintenance-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Database className="w-7 h-7 text-orange-500" />
            Mantenimiento <Badge variant="destructive" className="ml-2">MODO TEST</Badge>
          </h1>
          <p className="text-muted-foreground mt-1">
            Purga selectiva de colecciones de prueba. Dry-run + doble confirmación + auditoría.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={loadStats}
            disabled={refreshing}
            data-testid="maintenance-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refrescar
          </Button>
        </div>
      </div>

      {/* Warning banner */}
      <Card className="border-red-500/40 bg-red-50/40 dark:bg-red-950/20">
        <CardContent className="pt-6 flex gap-3">
          <AlertTriangle className="w-6 h-6 text-red-600 shrink-0" />
          <div className="text-sm space-y-1">
            <p className="font-semibold text-red-700 dark:text-red-300">
              Acciones IRREVERSIBLES. Pensadas para limpiar datos de prueba antes de producción.
            </p>
            <ul className="list-disc ml-5 text-red-700/90 dark:text-red-300/90">
              <li>Solo afecta tu DB activa ({data?.counts ? `${totalEligible} registros elegibles` : '...'}).</li>
              <li>El propio admin y el último admin del sistema están protegidos.</li>
              <li>Cada purga se registra en el log de Auditoría (módulo "Mantenimiento").</li>
              <li>Hacé siempre <strong>Vista previa</strong> antes de borrar.</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-muted-foreground">Cargando…</p>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: target list */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle>Colecciones</CardTitle>
              <CardDescription>Elegí qué querés revisar</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Target</TableHead>
                    <TableHead className="text-right">Registros</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {GROUP_ORDER.flatMap((group) => {
                    const inGroup = (data?.targets || []).filter(t => t.group === group);
                    if (inGroup.length === 0) return [];
                    const rows = [(
                      <TableRow key={`grp-${group}`} className="bg-muted/60 hover:bg-muted/60">
                        <TableCell colSpan={2} className="py-1.5">
                          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {group}{['Compras', 'Enfermería'].includes(group) && ' · DATOS REALES'}
                          </span>
                        </TableCell>
                      </TableRow>
                    )];
                    inGroup.forEach((t) => {
                      let count;
                      if (t.key === 'stock_reset') {
                        count = counts.articles_with_stock || 0;
                      } else if (t.key === 'treasury_reset') {
                        count = TREASURY_TX.reduce((acc, k) => acc + (counts[k] || 0), 0);
                      } else if (t.key === 'accounting_reset') {
                        count = ACCOUNTING_TX.reduce((acc, k) => acc + (counts[k] || 0), 0);
                      } else if (t.key === 'residents_reset') {
                        count = RESIDENTS_TX.reduce((acc, k) => acc + (counts[k] || 0), 0);
                      } else if (t.key === 'test_modules_reset') {
                        count = [...ACCOUNTING_TX, ...TREASURY_TX, ...RESIDENTS_TX].reduce((acc, k) => acc + (counts[k] || 0), 0);
                      } else {
                        count = counts[t.key] || 0;
                      }
                      const isSelected = selected?.key === t.key;
                      const isReset = t.key.endsWith('_reset');
                      rows.push(
                        <TableRow
                          key={t.key}
                          onClick={() => openTarget(t)}
                          data-testid={`target-row-${t.key}`}
                          className={`cursor-pointer ${isSelected ? 'bg-accent' : ''}`}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {t.real && <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                              <span className={
                                t.real ? 'text-red-700 dark:text-red-300'
                                  : isReset ? 'font-semibold text-orange-700 dark:text-orange-300' : ''
                              }>
                                {t.label}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{count}</TableCell>
                        </TableRow>
                      );
                    });
                    return rows;
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Right: target detail + filters + actions */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>
                {selected ? selected.label : 'Elegí un target a la izquierda'}
              </CardTitle>
              {selected && (
                <CardDescription>
                  {selected.real && (
                    <span className="block mb-1 font-semibold text-red-600" data-testid="real-data-warning">{REAL_DATA_NOTE}</span>
                  )}
                  {TARGET_DESCRIPTIONS[selected.key]}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {!selected ? (
                <p className="text-sm text-muted-foreground">
                  Las acciones aparecerán acá una vez seleccionada una colección.
                </p>
              ) : (
                <div className="space-y-4" data-testid="maintenance-detail">
                  {/* Filters */}
                  <div className="grid sm:grid-cols-2 gap-3">
                    {supportedFilters.includes('desde') && (
                      <div>
                        <Label htmlFor="f-desde">Desde</Label>
                        <Input
                          id="f-desde"
                          type="date"
                          value={filters.desde || ''}
                          onChange={(e) => setFilters({ ...filters, desde: e.target.value })}
                          data-testid="filter-desde"
                        />
                      </div>
                    )}
                    {supportedFilters.includes('hasta') && (
                      <div>
                        <Label htmlFor="f-hasta">Hasta</Label>
                        <Input
                          id="f-hasta"
                          type="date"
                          value={filters.hasta || ''}
                          onChange={(e) => setFilters({ ...filters, hasta: e.target.value })}
                          data-testid="filter-hasta"
                        />
                      </div>
                    )}
                    {supportedFilters.includes('warehouse_id') && (
                      <div>
                        <Label htmlFor="f-wh">Depósito</Label>
                        <select
                          id="f-wh"
                          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                          value={filters.warehouse_id || ''}
                          onChange={(e) => setFilters({ ...filters, warehouse_id: e.target.value })}
                          data-testid="filter-warehouse"
                        >
                          <option value="">(Todos)</option>
                          {data?.warehouses?.map(w => (
                            <option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {supportedFilters.includes('cash_bank_id') && (
                      <div>
                        <Label htmlFor="f-cb">Caja / Banco</Label>
                        <select
                          id="f-cb"
                          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                          value={filters.cash_bank_id || ''}
                          onChange={(e) => setFilters({ ...filters, cash_bank_id: e.target.value })}
                          data-testid="filter-cashbank"
                        >
                          <option value="">(Todas)</option>
                          {data?.cash_banks?.map(cb => (
                            <option key={cb.cash_bank_id} value={cb.cash_bank_id}>
                              {cb.name} ({cb.entity_type === 'cash' ? 'Caja' : 'Banco'} · {cb.currency})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {supportedFilters.includes('reset_initial_balances') && (
                      <div className="sm:col-span-2 border border-red-300 dark:border-red-700 rounded-md p-3 bg-red-50/40 dark:bg-red-950/30">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!filters.reset_initial_balances}
                            onChange={(e) => setFilters({ ...filters, reset_initial_balances: e.target.checked })}
                            className="mt-1 w-4 h-4"
                            data-testid="filter-reset-initial"
                          />
                          <div className="text-sm">
                            <p className="font-semibold text-red-700 dark:text-red-300">
                              También resetear saldos iniciales (initial_balance = 0)
                            </p>
                            <p className="text-xs text-red-700/80 dark:text-red-300/80 mt-1">
                              Por defecto el reset respeta los saldos de apertura de cada caja/banco
                              (lo que tenías en el día 1). Marcá esto SÓLO si esos saldos también son
                              de prueba y querés todas las cuentas en cero.
                            </p>
                          </div>
                        </label>
                      </div>
                    )}
                    {supportedFilters.includes('type') && (
                      <div>
                        <Label htmlFor="f-type">Tipo</Label>
                        <select
                          id="f-type"
                          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                          value={filters.type || ''}
                          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                          data-testid="filter-type"
                        >
                          <option value="">(Todos)</option>
                          <option value="entry">entry</option>
                          <option value="exit">exit</option>
                        </select>
                      </div>
                    )}
                    {supportedFilters.includes('modulo') && (
                      <div className="sm:col-span-2">
                        <Label htmlFor="f-mod">Módulo</Label>
                        <Input
                          id="f-mod"
                          value={filters.modulo || ''}
                          placeholder="Ej: Pedidos Internos"
                          onChange={(e) => setFilters({ ...filters, modulo: e.target.value })}
                          data-testid="filter-module"
                        />
                      </div>
                    )}
                    {supportedFilters.includes('status') && (
                      <div className="sm:col-span-2">
                        <Label htmlFor="f-status">Estado</Label>
                        <Input
                          id="f-status"
                          value={filters.status || ''}
                          placeholder="closed | cancelled | rejected | dispensed | …"
                          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                          data-testid="filter-status"
                        />
                      </div>
                    )}
                    {supportedFilters.includes('is_medicamento') && (
                      <div>
                        <Label htmlFor="f-med">Es medicamento</Label>
                        <select
                          id="f-med"
                          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                          value={filters.is_medicamento === undefined ? '' : String(filters.is_medicamento)}
                          onChange={(e) => {
                            const v = e.target.value;
                            setFilters({ ...filters, is_medicamento: v === '' ? undefined : v === 'true' });
                          }}
                          data-testid="filter-is-medicamento"
                        >
                          <option value="">(Todos)</option>
                          <option value="true">Sí (medicamento)</option>
                          <option value="false">No (insumo)</option>
                        </select>
                      </div>
                    )}
                    {supportedFilters.includes('only_zero_stock') && (
                      <div className="flex items-end pb-1">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={!!filters.only_zero_stock}
                            onCheckedChange={(v) => setFilters({ ...filters, only_zero_stock: !!v })}
                            data-testid="filter-only-zero-stock"
                          />
                          Solo stock = 0
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Users selector */}
                  {supportedFilters.includes('user_ids') && (
                    <div className="border rounded-md p-3 max-h-64 overflow-y-auto">
                      <p className="text-sm font-medium mb-2">
                        Seleccioná usuarios a borrar (no podés borrarte a vos mismo)
                      </p>
                      <div className="space-y-1">
                        {data?.users?.map(u => {
                          const isSelf = u.user_id === user?.user_id;
                          const isAdminUser = u.role === 'admin';
                          const checked = selectedUsers.includes(u.user_id);
                          return (
                            <label
                              key={u.user_id}
                              className={`flex items-center gap-2 px-2 py-1 rounded text-sm ${
                                isSelf ? 'opacity-50' : 'hover:bg-accent cursor-pointer'
                              }`}
                            >
                              <Checkbox
                                checked={checked}
                                disabled={isSelf}
                                data-testid={`user-checkbox-${u.user_id}`}
                                onCheckedChange={(v) => {
                                  setSelectedUsers((prev) =>
                                    v ? [...prev, u.user_id] : prev.filter(id => id !== u.user_id)
                                  );
                                }}
                              />
                              <span className="flex-1">
                                {u.name} <span className="text-muted-foreground">({u.email})</span>
                              </span>
                              {isAdminUser && <Badge variant="outline" className="text-[10px]">admin</Badge>}
                              {isSelf && <Badge variant="secondary" className="text-[10px]">vos</Badge>}
                              {!u.is_active && <Badge variant="outline" className="text-[10px]">inactivo</Badge>}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Preview result */}
                  {preview && (
                    <div className="border rounded-md p-3 bg-muted/40">
                      <p className="text-sm">
                        Coincidencias con los filtros actuales:{' '}
                        <span className="font-bold tabular-nums" data-testid="preview-matched">
                          {preview.matched}
                        </span>{' '}
                        registro(s).
                      </p>
                      {preview.matched === 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Ajustá los filtros — no hay nada que borrar con esa selección.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={onPreview}
                      disabled={previewing}
                      data-testid="preview-btn"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      {previewing ? 'Calculando…' : 'Vista previa'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={onExport}
                      disabled={exporting}
                      data-testid="export-btn"
                      title="Descarga un JSON con los registros que matchean los filtros. No borra nada."
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {exporting ? 'Generando…' : 'Descargar copia (.json)'}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={onAskConfirm}
                      disabled={!preview || preview.matched === 0}
                      data-testid="purge-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Borrar {preview ? `(${preview.matched})` : ''}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirmar borrado
            </DialogTitle>
            <DialogDescription>
              Vas a borrar <strong>{preview?.matched}</strong> registro(s) de{' '}
              <strong>{selected?.label}</strong>. Esta acción es <strong>irreversible</strong>.
              <span className="block mt-2 text-emerald-700 dark:text-emerald-400">
                🛡 Antes de borrar se descargará automáticamente un backup <code>.json</code> de seguridad con todo lo que se va a eliminar. Si la descarga falla, no se borra nada.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="confirm-input">Tipeá <code className="bg-muted px-1.5 py-0.5 rounded">BORRAR</code> para confirmar</Label>
            <Input
              id="confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="BORRAR"
              autoFocus
              data-testid="confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="confirm-cancel">
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={onPurge}
              disabled={confirmText !== 'BORRAR' || purging}
              data-testid="confirm-purge"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {purging ? 'Backup + borrando…' : 'Backup y borrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Maintenance;
